import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import {
  Undo2, Redo2, ZoomIn, ZoomOut, RotateCcw, Maximize,
  ClipboardCopy, Download, Grid3x3, Play, ShieldCheck,
} from 'lucide-react';

export function CanvasToolbar({
  canvasRef, canvasZoom,
  canUndo, canRedo, onUndo, onRedo,
  snapToGrid, setSnapToGrid,
  showSimulator, setShowSimulator,
  onVerify, onCopyCanvas, onDownloadPng,
}) {
  const btn = (active) => `p-1.5 hover:bg-muted rounded transition-colors ${active ? 'text-primary' : 'text-muted-foreground'}`;
  const disabledBtn = (enabled) => `p-1.5 hover:bg-muted rounded transition-colors ${enabled ? 'text-muted-foreground' : 'text-muted-foreground/30'}`;
  const sep = <div className="w-px h-4 bg-border mx-0.5" />;

  return (
    <div className="absolute bottom-4 right-4 glass-panel rounded-lg flex items-center gap-1 p-1 z-20" data-testid="canvas-toolbar">
      {/* Undo / Redo */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button onClick={onUndo} disabled={!canUndo} className={disabledBtn(canUndo)} data-testid="undo-btn">
            <Undo2 className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Undo (Ctrl+Z)</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <button onClick={onRedo} disabled={!canRedo} className={disabledBtn(canRedo)} data-testid="redo-btn">
            <Redo2 className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Redo (Ctrl+Shift+Z)</TooltipContent>
      </Tooltip>
      {sep}

      {/* Zoom controls */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button onClick={() => canvasRef.current?.zoomOut()} className="p-1.5 hover:bg-muted rounded transition-colors" data-testid="zoom-out-btn">
            <ZoomOut className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Zoom Out</TooltipContent>
      </Tooltip>
      <button
        onClick={() => canvasRef.current?.resetView()}
        className="px-2 py-1 text-xs font-mono hover:bg-muted rounded transition-colors min-w-[48px] text-center"
        data-testid="zoom-level-btn"
      >
        {Math.round(canvasZoom * 100)}%
      </button>
      <Tooltip>
        <TooltipTrigger asChild>
          <button onClick={() => canvasRef.current?.zoomIn()} className="p-1.5 hover:bg-muted rounded transition-colors" data-testid="zoom-in-btn">
            <ZoomIn className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Zoom In</TooltipContent>
      </Tooltip>
      {canvasZoom !== 1 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button onClick={() => canvasRef.current?.resetView()} className="p-1.5 hover:bg-muted rounded transition-colors text-muted-foreground" data-testid="zoom-reset-btn">
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Reset View</TooltipContent>
        </Tooltip>
      )}
      {sep}
      <Tooltip>
        <TooltipTrigger asChild>
          <button onClick={() => canvasRef.current?.fitToScreen()} className="p-1.5 hover:bg-muted rounded transition-colors" data-testid="fit-to-screen-btn">
            <Maximize className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Fit to Screen</TooltipContent>
      </Tooltip>
      {sep}

      {/* Copy & Download */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button onClick={onCopyCanvas} className="p-1.5 hover:bg-muted rounded transition-colors text-muted-foreground" data-testid="copy-canvas-btn">
            <ClipboardCopy className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Copy image to clipboard</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <button onClick={onDownloadPng} className="p-1.5 hover:bg-muted rounded transition-colors text-muted-foreground" data-testid="download-canvas-btn">
            <Download className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Download as PNG</TooltipContent>
      </Tooltip>
      {sep}

      {/* Snap to grid */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button onClick={() => setSnapToGrid(s => !s)} className={btn(snapToGrid)} data-testid="snap-grid-btn">
            <Grid3x3 className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{snapToGrid ? 'Disable' : 'Enable'} snap to grid</TooltipContent>
      </Tooltip>
      {sep}

      {/* Simulator toggle */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button onClick={() => setShowSimulator(s => !s)} className={btn(showSimulator)} data-testid="toggle-simulator-btn">
            <Play className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{showSimulator ? 'Close simulator' : 'Test input string'}</TooltipContent>
      </Tooltip>
      {sep}

      {/* Verify */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button onClick={onVerify} className="p-1.5 hover:bg-muted rounded transition-colors text-muted-foreground" data-testid="verify-btn">
            <ShieldCheck className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Verify DFA</TooltipContent>
      </Tooltip>
    </div>
  );
}
